import { rem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AI_DRAFT_KIND_LABEL, AI_DRAFT_KINDS, type AiDraftKind } from '@reditelji/shared';
import { useEffect, useState } from 'react';
import { useAiMutations, type AiDraft } from '../api/ai';
import { AiPill, hhmm } from '../components/AiBits';
import { CARD, INPUT, SECTION_LABEL, seg } from './ui';

const VIOLET = {
  on: { background: 'oklch(0.955 0.025 285)', color: 'oklch(0.34 0.07 285)', border: '1px solid oklch(0.86 0.05 285)' },
} as const;

/**
 * OSNUTEK ODGOVORA — preset (reply / chase / custom) + optional instruction →
 * Gemini draft in the volunteer's language. The draft is editable and can be
 * sent from the app (logged into the thread) or copied to the mailbox.
 */
export function AiDraftCard({
  volunteerId,
  volunteerEmail,
  enabled,
  defaultKind,
}: {
  volunteerId: string;
  volunteerEmail: string | null;
  enabled: boolean;
  defaultKind: AiDraftKind;
}) {
  const mut = useAiMutations();
  const [kind, setKind] = useState<AiDraftKind>(defaultKind);
  const [instruction, setInstruction] = useState('');
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [copied, setCopied] = useState(false);

  // a different person → start clean
  useEffect(() => {
    setKind(defaultKind);
    setInstruction('');
    setDraft(null);
    setSubject('');
    setBody('');
  }, [volunteerId]);

  const generating = mut.createDraft.isPending;
  const sending = mut.sendEmail.isPending;
  const canGenerate = enabled && !generating && (kind !== 'custom' || instruction.trim().length > 0);
  const canSend = Boolean(volunteerEmail) && !sending && subject.trim().length > 0 && body.trim().length > 0;

  const generate = () =>
    mut.createDraft.mutate(
      { volunteerId, kind, instruction: instruction.trim() },
      {
        onSuccess: (d) => {
          setDraft(d);
          setSubject(d.subject);
          setBody(d.body);
        },
      },
    );

  const send = () =>
    mut.sendEmail.mutate(
      { volunteerId, subject: subject.trim(), body: body.trim(), draftId: draft?.id },
      {
        onSuccess: () => {
          notifications.show({ message: 'E-pošta poslana — sporočilo je v pogovoru.' });
          setDraft(null);
          setSubject('');
          setBody('');
          setInstruction('');
        },
      },
    );

  const copy = () => {
    void navigator.clipboard.writeText(`Zadeva: ${subject}\n\n${body}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <div style={{ ...CARD, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={SECTION_LABEL}>OSNUTEK ODGOVORA</span>
        <div style={{ flex: 1 }} />
        <AiPill>{enabled ? 'Gemini · osnutek' : 'AI izklopljen'}</AiPill>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {AI_DRAFT_KINDS.map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            disabled={!enabled}
            style={{
              cursor: enabled ? 'pointer' : 'default',
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: rem(12.5),
              fontFamily: 'inherit',
              textAlign: 'left',
              ...(kind === k ? VIOLET.on : seg(false)),
            }}
          >
            {AI_DRAFT_KIND_LABEL[k]}
          </button>
        ))}
      </div>

      <textarea
        value={instruction}
        disabled={!enabled}
        onChange={(e) => {
          setInstruction(e.currentTarget.value);
          if (e.currentTarget.value.trim()) setKind('custom');
        }}
        placeholder="Dodatno navodilo za osnutek, npr. »ponudi mu izmeno na Kr3 popoldne in vprašaj za prevoz«"
        style={{
          ...INPUT,
          width: '100%',
          boxSizing: 'border-box',
          minHeight: 62,
          resize: 'vertical',
          lineHeight: 1.45,
          marginTop: 8,
          background: enabled ? 'oklch(0.99 0.003 95)' : 'oklch(0.985 0.004 95)',
        }}
      />
      <button
        onClick={generate}
        disabled={!canGenerate}
        title={enabled ? undefined : 'AI pomočnik ni vklopljen (AI_ENABLED)'}
        style={{
          width: '100%',
          marginTop: 8,
          padding: 9,
          borderRadius: 7,
          cursor: canGenerate ? 'pointer' : 'default',
          border: `1px solid ${canGenerate ? 'oklch(0.55 0.07 285)' : 'oklch(0.9 0.006 95)'}`,
          background: canGenerate ? 'oklch(0.62 0.08 285)' : 'oklch(0.95 0.005 95)',
          color: canGenerate ? 'oklch(1 0 0)' : 'oklch(0.6 0.012 90)',
          fontSize: rem(13),
          fontWeight: 600,
          fontFamily: 'inherit',
        }}
      >
        {generating ? 'Pišem osnutek…' : draft ? 'Ustvari nov osnutek' : 'Ustvari osnutek z Geminijem'}
      </button>

      {draft ? (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <div style={{ fontSize: rem(11.5), color: 'oklch(0.6 0.012 90)' }}>
            {AI_DRAFT_KIND_LABEL[draft.kind]} · {draft.model} · {hhmm(draft.createdAt)} · {draft.lang === 'en' ? 'ENG' : 'SLO'}
          </div>
          <input
            value={subject}
            onChange={(e) => setSubject(e.currentTarget.value)}
            placeholder="Zadeva"
            style={{ ...INPUT, width: '100%', boxSizing: 'border-box', fontWeight: 600 }}
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            style={{
              ...INPUT,
              width: '100%',
              boxSizing: 'border-box',
              minHeight: 220,
              resize: 'vertical',
              lineHeight: 1.55,
              background: 'oklch(0.978 0.004 95)',
            }}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              onClick={send}
              disabled={!canSend}
              title={volunteerEmail ? undefined : 'prostovoljec nima e-naslova'}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 7,
                cursor: canSend ? 'pointer' : 'default',
                border: `1px solid ${canSend ? 'oklch(0.55 0.09 155)' : 'oklch(0.9 0.006 95)'}`,
                background: canSend ? 'oklch(0.62 0.09 155)' : 'oklch(0.95 0.005 95)',
                color: canSend ? 'oklch(1 0 0)' : 'oklch(0.6 0.012 90)',
                fontSize: rem(13),
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              {sending ? 'Pošiljam…' : 'Pošlji e-pošto'}
            </button>
            <button
              onClick={copy}
              style={{
                padding: '8px 12px',
                borderRadius: 7,
                cursor: 'pointer',
                border: '1px solid oklch(0.9 0.006 95)',
                background: 'oklch(0.985 0.004 95)',
                fontSize: rem(13),
                fontFamily: 'inherit',
                color: 'oklch(0.38 0.012 90)',
              }}
            >
              {copied ? 'Kopirano ✓' : 'Kopiraj besedilo'}
            </button>
          </div>
          <div style={{ fontSize: rem(11.5), color: 'oklch(0.64 0.012 90)', lineHeight: 1.4 }}>
            Osnutek pred pošiljanjem preglej in uredi — pošlje se, kar je v poljih zgoraj.
          </div>
        </div>
      ) : null}
    </div>
  );
}
